/**
 * autoPreload.js — Programador simple de auto-descarga offline.
 *
 * Guarda en localStorage: {enabled: bool, hour: "06:00", ciudades: [str] | null, lastRun: iso}
 * Al iniciar la app y cada minuto, verifica si toca correr. Si es hora y
 * hoy no se ha ejecutado, descarga el snapshot y guarda en IndexedDB.
 */
import { preloadVisita } from "./offlineStore";

const KEY = "beem_autopreload_v1";

export function getAutoPreloadConfig() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { enabled: false, hour: "06:00", ciudades: null, lastRun: null };
    return { enabled: false, hour: "06:00", ciudades: null, lastRun: null, ...JSON.parse(raw) };
  } catch { return { enabled: false, hour: "06:00", ciudades: null, lastRun: null }; }
}

export function setAutoPreloadConfig(cfg) {
  try {
    const cur = getAutoPreloadConfig();
    localStorage.setItem(KEY, JSON.stringify({ ...cur, ...cfg }));
  } catch { /* fail-silent */ }
}

function _todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function _daysSince(iso) {
  if (!iso) return Infinity;
  try {
    // iso puede ser YYYY-MM-DD o ISO datetime
    const d = iso.length === 10 ? new Date(iso + "T00:00:00") : new Date(iso);
    return Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
  } catch { return Infinity; }
}

function _shouldRunNow(cfg) {
  if (!cfg.enabled) return false;
  if (!navigator.onLine) return false;
  const [hh, mm] = (cfg.hour || "06:00").split(":").map(Number);
  const now = new Date();
  const cur = now.getHours() * 60 + now.getMinutes();
  const target = hh * 60 + mm;
  const inWindow = cur >= target && cur < target + 60;
  if (!inWindow) return false;
  // Respetar frecuencia
  const freq = cfg.frecuencia || "diaria";
  const days = _daysSince(cfg.lastRun);
  const minDays = { diaria: 1, semanal: 7, quincenal: 15, mensual: 30 }[freq] || 1;
  // Si nunca corrió → sí. Si ya pasó el mínimo → sí.
  return cfg.lastRun == null || days >= minDays;
}

let _checking = false;

async function _runOnce(apiClient) {
  if (_checking) return;
  _checking = true;
  try {
    const cfg = getAutoPreloadConfig();
    if (!_shouldRunNow(cfg)) return;
    const body = {};
    if (cfg.departamentos && cfg.departamentos.length) body.departamentos = cfg.departamentos;
    if (cfg.zonas && cfg.zonas.length) body.zonas = cfg.zonas;
    if (cfg.ciudades && cfg.ciudades.length) body.ciudades = cfg.ciudades;
    const { data } = await apiClient.post("/pedidos/_/precargar-visita", body);
    await preloadVisita(data);
    setAutoPreloadConfig({ lastRun: _todayStr() });
    const m = data.meta || {};
    // eslint-disable-next-line no-console
    console.info(`[Beem] Auto-precarga: ${m.clientes_count} clientes · ${m.productos_count} productos guardados`);
    // Notificación push nativa (si el usuario dio permiso)
    _notifyPush(m);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[Beem] Auto-precarga falló:", e?.message || e);
    _notifyPush(null, e?.message || "Sin conexión");
  } finally {
    _checking = false;
  }
}

function _notifyPush(meta, errorMsg) {
  try {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission !== "granted") return;
    if (errorMsg) {
      new Notification("Beem · auto-descarga falló", {
        body: `No se pudo preparar tu ruta: ${errorMsg}. Reintentamos al reconectar.`,
        icon: "/favicon.ico",
        tag: "beem-autopreload",
      });
      return;
    }
    const n = new Notification("Beem · listo para tu ruta", {
      body: `${meta.clientes_count} clientes · ${meta.productos_count} productos · ${meta.precios_entries} precios cargados en tu celular`,
      icon: "/favicon.ico",
      tag: "beem-autopreload",
      silent: false,
    });
    // Auto-cerrar a los 10s para no saturar
    setTimeout(() => { try { n.close(); } catch {} }, 10000);
  } catch { /* fail-silent */ }
}

/** Pide permiso de notificaciones. Devuelve la decisión final ("granted"|"denied"|"default"). */
export async function requestNotificationPermission() {
  if (typeof window === "undefined" || !("Notification" in window)) return "denied";
  if (Notification.permission === "granted" || Notification.permission === "denied") return Notification.permission;
  try {
    return await Notification.requestPermission();
  } catch { return "denied"; }
}

export function startAutoPreloadScheduler(apiClient) {
  if (typeof window === "undefined") return;
  // Al arrancar la app
  setTimeout(() => _runOnce(apiClient), 3000);
  // Cada minuto revisa si toca
  setInterval(() => _runOnce(apiClient), 60000);
  // Al reconectar (si perdió la ventana por estar offline)
  window.addEventListener("online", () => setTimeout(() => _runOnce(apiClient), 2000));
}
